import styled from '@emotion/styled';
import DOMPurify from 'dompurify';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { i18n, mediaQuery, openCallMeModal, RootState, useAppDispatch, useAppSelector } from 'src/common';
import { useGetOfferQuery } from 'src/common/api/apollo/additional';
import { Button, Card, Flex, Loader, Text } from 'src/components';
import { colors, footer, header } from 'src/theme';
import { AppRoutesAdditional } from '../../_router/app.routes';


const FlexStyle = styled(Flex)({
  minHeight: `calc(100vh - ${footer.mobileHeight + header.mobileHeight + 80}px)`,

  [mediaQuery('mobile')]: {
    minHeight: `calc(100vh - ${footer.height + header.mobileHeight + 80}px)`,
  },

  [mediaQuery('laptopL')]: {
    minHeight: `calc(100vh - ${footer.height + header.height + 80}px)`,
  },
});


const TitleTextStyle = styled.div({
  textAlign: 'left',
  width: '100%',
});

const CardWrapperStyle = styled.div({
  width: '100%',
  maxWidth: '80%',
  alignSelf: 'start',
});

const TextLinkStyle = styled.a({
  display: 'inline-block',
  cursor: 'pointer',
  fontSize: 16,
  fontWeight: 700,
  color: colors.text2,
  textDecoration: 'none',

  '&:hover': {
    color: colors.primary,
  },
});

const ButtonRowStyle = styled(Flex)({
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
  alignSelf: 'stretch',
});

const DescriptionHtml = styled.div({
  fontSize: 16,
  lineHeight: '24px',
  color: colors.text1,
  fontFamily: '"Red Hat Display", sans-serif',
  fontWeight: 400,
  width: '100%',
  '& p': {
    margin: '0 0 16px 0',
    '&:last-child': {
      marginBottom: 0,
    },
  },
});

const HealthQuestionnaireConfirmationNeededPage = () => {
  const navigate = useNavigate();
  const params = useParams<{ offerId: string }>();
  const dispatch = useAppDispatch();
  const flow = useAppSelector((store: RootState) => store.state.flow);
  
  const { data: getOfferAdditionalData, loading: loadingOfferAdditional } = useGetOfferQuery({
    variables: { id: params.offerId || '' },
    skip: !params.offerId,
    onError: () => {
      navigate(AppRoutesAdditional.OFFERS_PREVIEW.fullPath);
    },
    onCompleted: (data) => {
      if (data.getOffer?.productTypeSpecific?.healthQuestionnaire?.isConfirmed ?? false) {
        navigate(`${AppRoutesAdditional.OFFER.fullPath}/${params.offerId}`);
      }
    }
  });


  if (loadingOfferAdditional) return <Loader page />;
  if (!getOfferAdditionalData?.getOffer) return null;

  return (
    <FlexStyle justify="center" align="center">
      <CardWrapperStyle>
        <Card padding={30}>
          <Flex vertical gap={24} align="start">
            <Flex vertical align="start" gap={16}>
              <TitleTextStyle>
                <Text type="large" bold>
                  {i18n.t('pages.healthQuestionnaireConfirmationNeeded.title')}
                </Text>
              </TitleTextStyle>
              <DescriptionHtml
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    getOfferAdditionalData.getOffer?.product?.brand?.code === 'uniqa' ? i18n.t('pages.healthQuestionnaireConfirmationNeeded.uniqaDescription') : i18n.t('pages.healthQuestionnaireConfirmationNeeded.triglavDescription'),
                  ),
                }}
              />
            </Flex>
            <Flex justify="space-between" align="center" gap={16} style={{ width: '100%', alignSelf: 'stretch' }}>
              <ButtonRowStyle gap={16}>
                <TextLinkStyle
                  href={flow.flowType === 'agent' ? `${AppRoutesAdditional.AGENT_OFFERS_PREVIEW.fullPath}/${getOfferAdditionalData.getOffer?.soId}` : `${AppRoutesAdditional.OFFERS_PREVIEW.fullPath}`} 
                >
                  {i18n.t('pages.healthQuestionnaireConfirmationNeeded.closeButton')}
                </TextLinkStyle>
              </ButtonRowStyle>
              <Button
                text={i18n.t('pages.tooManyDiseases.requestCallButton')}
                onClick={() => dispatch(openCallMeModal())}
              />
            </Flex>
          </Flex>
        </Card>
      </CardWrapperStyle>
    </FlexStyle >
  );
};

export default HealthQuestionnaireConfirmationNeededPage;